import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

export default function TeamCRUD() {
    const [teams, setTeams] = useState([]);
    const [newTeam, setNewTeam] = useState({ name: "", description: "" });
    const [editingId, setEditingId] = useState(null);
    const [editTeam, setEditTeam] = useState({ name: "", description: "" });
    const navigate = useNavigate();

    // Fetch all teams
    const fetchTeams = async () => {
        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `${API_BASE_URL}/api/teams`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = await res.json();
            setTeams(data);
        } catch (error) {
            toast.error("Failed to load teams.");
        }
    };


    useEffect(() => {
        fetchTeams();
    }, []);

    // Authentication check
    useEffect(() => {
        const token = localStorage.getItem("token");
        const roles = JSON.parse(localStorage.getItem("roles") || "[]");

        if (!token) {
            toast.error("You must be logged in to access this page.");
            navigate("/");
            return;
        }

        // ✅ check admin role
        if (!roles.includes("ROLE_ADMIN")) {
            toast.error("You are not authorized to access this page.");
            navigate("/");
            return;
        }
    }, [navigate]);

    // Add new team
    const handleAdd = async () => {
        if (!newTeam.name.trim()) {
            toast.warning("Please enter a team name.");
            return;
        }

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `${API_BASE_URL}/api/teams`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(newTeam),
                }
            );

            if (res.ok) {
                toast.success("Team added successfully!");
                setNewTeam({ name: "", description: "" });
                fetchTeams();
            } else {
                const msg = await res.text();
                toast.error("Description should less than 255 character");
            }
        } catch {
            toast.error("Error adding team.");
        }
    };


    // Update team
    const handleUpdate = async (id) => {
        if (!editTeam.name.trim()) {
            toast.warning("Please enter a valid team name.");
            return;
        }

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `${API_BASE_URL}/api/teams/${id}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(editTeam),
                }
            );

            if (res.ok) {
                toast.success("Team updated!");
                setEditingId(null);
                setEditTeam({ name: "", description: "" });
                fetchTeams();
            } else {
                const msg = await res.text();
                toast.error(msg);
            }
        } catch {
            toast.error("Error updating team.");
        }
    };


    // Delete team
    const handleDelete = async (id) => {
        if (!window.confirm("Delete this team?")) return;

        try {
            const token = localStorage.getItem("token");

            const res = await fetch(
                `${API_BASE_URL}/api/teams/${id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (res.ok) {
                toast.info("Team deleted.");
                fetchTeams();
            } else {
                const msg = await res.text();
                toast.error(msg);
            }
        } catch {
            toast.error("Error deleting team.");
        }
    };


    return (
        <>
            {/* Fixed Navbar */}
            <div className="fixed-top">
                <AdminNavbar />
            </div>

            {/* Full-page layout */}
            <div
                className="d-flex flex-column p-4"
                style={{
                    minHeight: "100vh",
                    backgroundColor: "#F5F7FB",
                    marginTop: "110px",
                }}
            >
                <h3 className="fw-bold text-dark mb-4 text-start">
                    Team Management
                </h3>

                <div className="row flex-grow-1 g-4">
                    {/* Left Column - Add Team */}
                    <div className="col-md-4 d-flex">
                        <div
                            className="card shadow-sm border-0 flex-fill"
                            style={{
                                borderRadius: "12px",
                                height: "fit-content",
                                alignSelf: "flex-start",
                            }}
                        >
                            <div
                                className="card-header fw-semibold"
                                style={{
                                    backgroundColor: "#DFE4EB",
                                    color: "black",
                                    borderTopLeftRadius: "12px",
                                    borderTopRightRadius: "12px",
                                }}
                            >
                                Add New Team
                            </div>
                            <div className="card-body">
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="Team Name"
                                    value={newTeam.name}
                                    onChange={(e) =>
                                        setNewTeam({ ...newTeam, name: e.target.value })
                                    }
                                />
                                <input
                                    type="text"
                                    className="form-control mb-3"
                                    placeholder="Description"
                                    value={newTeam.description}
                                    onChange={(e) =>
                                        setNewTeam({ ...newTeam, description: e.target.value })
                                    }
                                />
                                <button
                                    className="btn text-dark w-40"
                                    style={{ backgroundColor: "#DFE4EB" }}
                                    onClick={handleAdd}
                                >
                                    <FaPlus className="me-2" />
                                    Add Team
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Team List */}
                    <div className="col-md-8 d-flex">
                        <div
                            className="card shadow-sm border-0 flex-fill"
                            style={{ borderRadius: "12px" }}
                        >
                            <div
                                className="card-header fw-semibold d-flex justify-content-between align-items-center"
                                style={{
                                    backgroundColor: "#DFE4EB",
                                    borderTopLeftRadius: "12px",
                                    borderTopRightRadius: "12px",
                                }}
                            >
                                <span>All Teams</span>
                                <span className="badge bg-secondary">{teams.length} Total</span>
                            </div>

                            <div
                                className="card-body p-0"
                                style={{ maxHeight: "70vh", overflowY: "auto" }}
                            >
                                <ul className="list-group list-group-flush">
                                    {teams.length === 0 && (
                                        <li className="list-group-item text-muted text-center py-3">
                                            No teams found.
                                        </li>
                                    )}
                                    {teams.map((team) => (
                                        <li
                                            key={team.id}
                                            className="list-group-item d-flex justify-content-between align-items-center"
                                        >
                                            {editingId === team.id ? (
                                                <div className="d-flex flex-column flex-md-row gap-2 w-100">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editTeam.name}
                                                        onChange={(e) =>
                                                            setEditTeam({ ...editTeam, name: e.target.value })
                                                        }
                                                    />
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={editTeam.description || ""}
                                                        onChange={(e) =>
                                                            setEditTeam({
                                                                ...editTeam,
                                                                description: e.target.value,
                                                            })
                                                        }
                                                    />
                                                </div>
                                            ) : (
                                                <div className="d-flex flex-column flex-md-row gap-2 w-100">
                                                    <span className="fw-medium">{team.name}</span>
                                                    <span>{team.description}</span>
                                                </div>
                                            )}

                                            <div>
                                                {editingId === team.id ? (
                                                    <>
                                                        <button
                                                            className="btn btn-success btn-sm me-2"
                                                            onClick={() => handleUpdate(team.id)}
                                                        >
                                                            <FaSave /> Save
                                                        </button>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={() => setEditingId(null)}
                                                        >
                                                            <FaTimes /> Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className="btn btn-sm"
                                                            onClick={() => {
                                                                setEditingId(team.id);
                                                                setEditTeam({
                                                                    name: team.name,
                                                                    description: team.description || "",
                                                                });
                                                            }}
                                                        >
                                                            <FaEdit />
                                                        </button>
                                                        <button
                                                            className="btn  btn-sm"
                                                            onClick={() => handleDelete(team.id)}
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>

                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
